import { LinkOutlined } from '@ant-design/icons';
import {
  EWithdrawalStatus,
  type IAdminWithdrawalDetail,
  type IAdminWithdrawalOrderCandidate,
} from '@mahawi/eshop-common/src/types';
import { Button, Input, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import {
  withdrawalLinkOrder,
  withdrawalOrderSearch,
} from '../../reducers/withdrawal/reducer';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import TableComponent from '../table';

type TCandidateRow = IAdminWithdrawalOrderCandidate & { key: string };

interface ILinkOrderPanelProps {
  detail: IAdminWithdrawalDetail;
  inProcess: boolean;
}

function LinkOrderPanel({
  detail,
  inProcess,
}: ILinkOrderPanelProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const searchResults = useSelector(
    (s: RootState) => s.Withdrawal.orderSearchResults,
  );

  const columns = useMemo<ColumnsType<TCandidateRow>>(
    () => [
      {
        title: 'Order',
        dataIndex: 'shortCode',
        render: (
          shortCode: string,
          record: TCandidateRow,
        ): React.ReactElement => (
          <Link to={`/order/${record.uuid}`} label={shortCode} openInNewTab />
        ),
      },
      { title: 'Billing name', dataIndex: 'billingName' },
      { title: 'Billing email', dataIndex: 'billingEmail' },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (createdAt: string): string =>
          dayjs(createdAt).format(EDateFormat.DATE),
      },
      { title: 'Status', dataIndex: 'orderStatusCode' },
      {
        title: 'Total',
        render: (_value: number, record: TCandidateRow): string =>
          `${record.total} ${record.currencyIsoCode}`,
      },
      {
        title: '',
        render: (_value: string, record: TCandidateRow): React.ReactElement => (
          <Button
            size="small"
            type="primary"
            icon={<LinkOutlined />}
            disabled={inProcess}
            onClick={(): void => {
              dispatch(withdrawalLinkOrder(detail.uuid, record.uuid));
            }}
          >
            Link
          </Button>
        ),
      },
    ],
    [detail.uuid, dispatch, inProcess],
  );

  const toRows = (
    candidates: IAdminWithdrawalOrderCandidate[] | undefined,
  ): TCandidateRow[] =>
    (candidates ?? []).map((candidate) => ({
      key: candidate.uuid,
      ...candidate,
    }));

  if (detail.order) {
    return (
      <Space size={SPACING.sm} wrap>
        <Typography.Text>Linked to order</Typography.Text>
        <Link
          to={`/order/${detail.order.uuid}`}
          label={detail.order.shortCode}
        />
        <Typography.Text type="secondary">
          {detail.order.orderStatusCode}
        </Typography.Text>
        <Button
          size="small"
          danger
          disabled={
            inProcess || detail.status === EWithdrawalStatus.MONEY_REFUNDED
          }
          onClick={(): void => {
            dispatch(withdrawalLinkOrder(detail.uuid, null));
          }}
        >
          Unlink
        </Button>
      </Space>
    );
  }

  return (
    <Space orientation="vertical" size={SPACING.md} style={{ width: '100%' }}>
      <Typography.Text strong>Suggested orders</Typography.Text>
      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={toRows(detail.suggestions)}
        isLoading={false}
        emptyText="No order matches the entered number or email"
      />
      <Input.Search
        placeholder="Search orders by short code, UUID or billing email"
        enterButton="Search"
        allowClear
        disabled={inProcess}
        onSearch={(query: string): void => {
          if (query.trim()) {
            dispatch(withdrawalOrderSearch(query.trim()));
          }
        }}
      />
      {searchResults && (
        <TableComponent
          columns={columns as Parameters<typeof TableComponent>[0]['columns']}
          dataSource={toRows(searchResults)}
          isLoading={inProcess}
          emptyText="No orders found"
        />
      )}
    </Space>
  );
}

export default LinkOrderPanel;
